<template>
    <div class="pp-playbar">
        <div v-if="currentTrack && currentTrack.album && currentTrack.album.artwork"  @click="navigateAlbum" class="pp-playbar__artwork">
            <img :src="currentTrack.album.artwork">
        </div>
        <div v-if="currentTrack" @click="navigateAlbum" class="pp-playbar__now-playing">
            <div class="pp-playbar__now-playing__track" :style="{ width: nowPlayingSize, overflow: 'hidden', textOverflow: 'ellipsis' }">
                {{ currentTrack.title }}
            </div>
            <div v-if="currentTrack.album && !currentTrack.album.exclusive_id" class="pp-playbar__now-playing__album" :style="{ width: nowPlayingSize, overflow: 'hidden', textOverflow: 'ellipsis' }">
                {{ currentTrack.album.title }}
            </div>
            <div v-if="currentTrack.album && currentTrack.album.exclusive_id && currentTrack.album.artists" class="pp-playbar__now-playing__album" :style="{ width: nowPlayingSize, overflow: 'hidden', textOverflow: 'ellipsis' }">
                {{ getExclusiveArtistLine(currentTrack.album) }}
            </div>
        </div>
        <div v-show="currentTrack" class="pp-playbar__padding"></div>
        <div class="pp-playbar__controls-container">
            <div class="pp-play-controls">
                <div class="pp-play-controls__main">
                    <ul ref="secondaryControls" class="pp-play-controls__main__secondary">
                        <li>
                            <button @click="toggleRepeat"
                                    title="Repeat"
                                    :class="{'pp-play-controls__button': 1, 'pp-play-controls__button--active': controls.repeat}">
                                <i class="fa fa-fw fa-refresh"></i>
                            </button>
                        </li>
                        <li>
                            <button @click="toggleShuffle"
                                    title="Shuffle"
                                    :class="{'pp-play-controls__button': 1, 'pp-play-controls__button--active': controls.shuffle}">
                                <i class="fa fa-fw fa-random"></i>
                            </button>
                        </li>
                        <!--
                            <li>
                                <button :disabled="!controls.castEnabled" class="pp-play-controls__button" title="Cast">
                                    <i class="fa fa-fw fa-podcast"></i>
                                </button>
                            </li>
                        -->
                        <li>
                            <button ref="toggleQueueButton"
                                    @click="toggleQuickQueue"
                                    title="Toggle queue"
                                    :class="{'pp-play-controls__button':1, 'pp-play-controls__button--active': controls.showQueue }">
                                <i class="fa fa-fw fa-indent"></i>
                            </button>

                            <div ref="queuePopup" v-show="controls.showQueue" class="pp-playbar__quickqueue">
                                <div class="pp-playbar__quickqueue__header">
                                    History <small v-if="history.length > 0">({{ history.length }} items)</small>
                                </div>
                                <mini-track-list :max-rows="2" :reversed="true"
                                                 :items="history"
                                                 context-mode="from-history"
                                                 zerostate="Empty"></mini-track-list>
                                <div class="pp-playbar__quickqueue__header">
                                    Now Playing
                                </div>
                                <mini-track-list :items="currentTrack ? [currentTrack] : []"
                                                 zerostate="Not playing"></mini-track-list>
                                <div class="pp-playbar__quickqueue__header">
                                    Up Next <small v-if="queue.length > 0">({{ queue.length }} items)</small>
                                </div>
                                <mini-track-list :max-rows="4" :items="queue"
                                                 context-mode="from-queue"
                                                 zerostate="Nothing in queue"></mini-track-list>
                            </div>
                        </li>
                    </ul>
                    <ul class="pp-play-controls__main__primary">
                        <li>
                            <button @click="backward" class="pp-play-controls__button pp-play-controls__button--primary">
                                <i class="fa fa-fw fa-backward"></i>
                            </button>
                        </li>
                        <li>
                            <button @click="play" class="pp-play-controls__button pp-play-controls__button--play">
                                <i :class="{'fa':1, 'fa-fw':1, 'fa-play': !playing , 'fa-pause': playing}"></i>
                            </button>
                        </li>
                        <li>
                            <button @click="forward" class="pp-play-controls__button pp-play-controls__button--primary">
                                <i class="fa fa-fw fa-forward"></i>
                            </button>
                        </li>
                    </ul>
                    <div class="pp-play-controls__volume">
                        <div class="pp-play-controls__volume__label">
                            <i class="fa fa-fw fa-volume-off"></i>
                        </div>
                        <input v-model="controls.volume" data-seekbar-fix class="pp-seekbar" type="range" max="100" min="0" value="50">
                        <div class="pp-play-controls__volume__label">
                            <i class="fa fa-fw fa-volume-up"></i>
                        </div>
                    </div>
                </div>
                <div class="pp-play-controls__seekbar">
                    <div class="pp-play-controls__seekbar__label">
                        {{ playbackPositionSeconds|duration }}
                    </div>
                    <input ref="seekbar" step="0.001" :value="controls.seeking ? controls.position : playbackPosition" data-seekbar-fix class="pp-seekbar pp-seekbar--subtle-thumb" type="range" max="100" min="0" value="33">
                    <div class="pp-play-controls__seekbar__label">
                        {{ playbackDuration|duration }}
                    </div>
                </div>
            </div>
        </div>
        <div v-show="currentTrack" class="pp-playbar__quality">
            <div class="pp-playbar__quality__label">
                MP3
            </div>
            <input :disabled="!enableFlac || !enableFlacTrack" v-model.number="flacInput" :debug.number="flacInput" data-seekbar-fix class="pp-seekbar pp-seekbar--togglebutton" type="range" max="1" min="0" value="1">
            <div class="pp-playbar__quality__label">
                FLAC
            </div>
            <div v-if="!enableFlac" class="pp-playbar__quality__disabled pp-playbar__quality__disabled--device"
                 title="To enable FLAC playback, please use a modern browser such as Chrome or Firefox.">
                Not supported by your device.
            </div>
            <div v-if="enableFlac && !enableFlacTrack" class="pp-playbar__quality__disabled pp-playbar__quality__disabled--device"
                 title="Unfortunately we don't have a copy of this track in FLAC at the yet.">
                Not available for this track.
            </div>
        </div>
        <a v-show="currentTrack && currentTrack.album && !$root.isIos"
           :href="'https://www.pristineclassical.com/products/'+((currentTrack && currentTrack.album) ? currentTrack.album.code.toLowerCase() : '')"
           target="_blank"
           class="pp-playbar__button">
            Buy Now
        </a>
    </div>
</template>


<script>
import Tether from 'tether';
import EventBus from '../EventBus.js';
import TrackQueue from '../TrackQueue.js';
import MiniTrackList from './MiniTrackList.vue';
import Vue from 'vue';

export default {
    name: 'pp-playbar',
    components: {
        MiniTrackList
    },
    data () {
        return {
            controls: {
                repeat: true,
                shuffle: false,
                cast: false,
                castEnabled: false,
                showQueue: false,

                seeking: false,
                position: 0,
                volume: 33,
                flac: true,
            },
            quickQueueTether: null,
            nowPlayingSize: 0,
        };
    },
    props: {
        enableFlac: Boolean,
        enableFlacTrack: Boolean,
        playing: Boolean,
        currentTrack: Object,
        playbackPosition: Number,
        playbackPositionSeconds: Number,
        playbackDuration: Number,
        queue: Array,
        history: Array,
    },
    computed: {
        flacInput: {
            get() {
                return this.controls.flac ? 1 : 0;
            },
            set(value) {
                this.controls.flac = value == 1;
            }
        }
    },
    mounted() {
        this.quickQueueTether = new Tether({
            element: this.$refs.queuePopup,
            target: this.$refs.toggleQueueButton,
            attachment: 'bottom center',
            targetAttachment: 'top center',
            offset: '12px 0'
        });

        console.log(this.quickQueueTether);

        // Update tether sizing after queue redraw
        TrackQueue.on('changed', () => {
            Vue.nextTick(() => {
                this.quickQueueTether.position();
            });
        });


        if (!this.enableFlac && this.controls.flac) {
            this.controls.flac = false;
        }

        EventBus.on('controls.setVolume', (e) => {
            if (e.data.volume != this.controls.volume) {
                this.controls.volume = e.data.volume;
            }
        });

        EventBus.on('controls.setQuality', (e) => {
            if (e.data.flac != this.controls.flac) {
                this.controls.flac = e.data.flac;
            }
        });

        this.$refs.seekbar.addEventListener('input', (e) => {
            this.controls.seeking = true;
            this.controls.position = e.target.value;
        });

        this.$refs.seekbar.addEventListener('change', (e) => {
            this.controls.seeking = false;

            EventBus.emit('controls.setPosition', {
                position: e.target.value,
                type: 'seek'
            });
        });


        window.addEventListener('resize', () => {
            this.resizeNowPlaying();
        });

        this.resizeNowPlaying();
    },
    methods: {
        getExclusiveArtistLine(album) {
            var artistLine = '';
            for (let artist of album.artists) {
                if (artistLine != '') {
                    artistLine += ', ';
                }
                artistLine += artist.display_name;
            }
            return artistLine;
        },

        resizeNowPlaying() {
            var nowPlayingSize = (window.innerWidth / 3) - 200;
            nowPlayingSize -= nowPlayingSize % 100;
            this.nowPlayingSize = nowPlayingSize + 'px';
            this.nowPlayingSize = nowPlayingSize + 'px';
        },

        navigateAlbum() {
            if (!this.currentTrack || !this.currentTrack.album);
            this.$router.push('/browse/albums/'+ this.currentTrack.album.id);
        },

        toggleRepeat () {
            console.log(this.controls);
            this.controls.repeat = !this.controls.repeat;
            EventBus.emit('controls.playModeChanged', {
                repeat: this.controls.repeat,
                shuffle: this.controls.shuffle
            });
        },
        toggleQuickQueue() {
            this.controls.showQueue = !this.controls.showQueue;
            if (this.controls.showQueue) {
                Vue.nextTick(() => {
                    if (this.quickQueueTether) {
                        this.quickQueueTether.position();
                    }
                });
            }
        },
        toggleShuffle() {
            this.controls.shuffle = !this.controls.shuffle;
            EventBus.emit('controls.playModeChanged', {
                repeat: this.controls.repeat,
                shuffle: this.controls.shuffle
            });
        },
        play() {
            EventBus.emit('controls.togglePause', {});
        },
        forward() {
            EventBus.emit('controls.next', {});
        },
        backward() {
            EventBus.emit('controls.previous', {});
        },
    },
    watch: {
        currentTrack: function currentTrack() {
            if (this.quickQueueTether) {
                Vue.nextTick(() => {
                    this.quickQueueTether.position();
                });
            }
        },
        'controls.flac': function flac() {
            if (this.controls.flac && !this.enableFlac) {
                this.controls.flac = false;
            }
            EventBus.emit('controls.setQuality', {
                flac: this.controls.flac == true
            });
        },
        'enableFlac': function enableFlac() {
            if (!this.enableFlac && this.controls.flac) {
                this.controls.flac = false;
                this.$emit('controls.setQuality', {
                    flac: this.controls.flac == true
                });
            }
        },
        'playbackPosition': function playbackPosition() {
            var e = new Event('seekbar.update');
            this.$refs.seekbar.dispatchEvent(e);
        },
        'controls.volume': function volume() {
            EventBus.emit('controls.setVolume', {
                volume: this.controls.volume
            });
        }
    }
}
</script>



// WEBPACK FOOTER //
// PpPlaybar.vue?d0f2d1aa